import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { Link, useSearchParams } from "react-router-dom";

import {
  decideDeviceLogin,
  getDeviceLogin,
} from "@/gen/nodebase/v1/account-AccountService_connectquery";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorNote, Loading } from "@/components/page";
import { errorReason } from "@/lib/format";
import { useSession, setSession } from "@/lib/session";

// The approval half of browser sign-in (control-api device_login.go). A
// device — Desktop, the CLI, a router — shows a code and sends the person
// here; this page names what is asking and lets them approve it for the
// signed-in account. It is reached signed out too: App renders the login and
// registration cards on /device and both come back here, so a new user
// registers and approves in one trip.
//
// The page's job beyond the button is letting a person notice a request they
// did not start: the device's own description, the code to compare with the
// device's screen, and whether the request came from this browser's network.

const PLATFORM_NAMES: Record<string, string> = {
  macos: "macOS",
  darwin: "macOS",
  windows: "Windows",
  linux: "Linux",
  openwrt: "OpenWrt router",
  android: "Android",
  ios: "iOS",
};

const CLIENT_NAMES: Record<string, string> = {
  desktop: "Nodebase Desktop",
  cli: "Nodebase CLI",
};

export function DeviceApprove() {
  const [params, setParams] = useSearchParams();
  const code = params.get("code")?.trim() ?? "";
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md" data-testid="device-card">
        {code ? (
          <DeviceRequest code={code} />
        ) : (
          <EnterCode onCode={(c) => setParams({ code: c }, { replace: true })} />
        )}
      </Card>
    </div>
  );
}

function EnterCode({ onCode }: { onCode: (code: string) => void }) {
  const [value, setValue] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    const c = value.trim();
    if (c) onCode(c);
  }
  return (
    <>
      <CardHeader>
        <CardTitle>Sign in a device</CardTitle>
        <CardDescription>
          Enter the code shown by Nodebase on your computer, terminal or router.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="device-code">Code</Label>
            <Input
              id="device-code"
              name="code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXX-XXXX"
              className="font-mono uppercase tracking-widest"
              autoFocus
              required
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <Button type="submit" className="w-full">
            Continue
          </Button>
        </form>
      </CardContent>
    </>
  );
}

function DeviceRequest({ code }: { code: string }) {
  const session = useSession();
  const [outcome, setOutcome] = useState<"approved" | "denied" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const request = useQuery(
    getDeviceLogin,
    { userCode: code },
    // A refusal here is final for this code (invalid, expired, decided) and a
    // retry only spends the account's lookup budget.
    { retry: false, enabled: outcome === null, refetchOnWindowFocus: false },
  );
  const decide = useMutation(decideDeviceLogin);

  async function answer(approve: boolean) {
    setError(null);
    try {
      await decide.mutateAsync({ userCode: code, approve });
      setOutcome(approve ? "approved" : "denied");
    } catch (err) {
      setError(err);
    }
  }

  const d = request.data;
  const deviceName = d?.deviceName || "A device";

  if (outcome === "approved") {
    return (
      <>
        <CardHeader>
          <CardTitle>Device signed in</CardTitle>
          <CardDescription data-testid="device-outcome">
            {deviceName} is now signed in to {session?.email}. You can close
            this page and return to it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">Open the Nodebase console</Link>
          </Button>
        </CardContent>
      </>
    );
  }
  if (outcome === "denied") {
    return (
      <CardHeader>
        <CardTitle>Request declined</CardTitle>
        <CardDescription data-testid="device-outcome">
          The device was not signed in. If you did not start this request,
          nothing else is needed.
        </CardDescription>
      </CardHeader>
    );
  }

  const invalid = errorReason(request.error) === "device-login-invalid";
  return (
    <>
      <CardHeader>
        <CardTitle>Sign in this device?</CardTitle>
        <CardDescription>
          Signed in as <span className="font-medium text-foreground">{session?.email}</span>.{" "}
          <button
            type="button"
            className="underline underline-offset-4"
            onClick={() => setSession(null)}
          >
            Use a different account
          </button>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {request.isPending ? (
          <Loading />
        ) : request.isError ? (
          invalid ? (
            <div role="alert" data-testid="device-invalid" className="nb-alert nb-alert--danger text-sm">
              This code is not valid any more — it may have expired or already
              been used. Start signing in again on your device to get a new code.
            </div>
          ) : (
            <ErrorNote error={request.error} />
          )
        ) : (
          <>
            <div className="rounded-md border p-3 text-sm" data-testid="device-details">
              <p className="text-base font-medium">{deviceName}</p>
              <p className="text-muted-foreground">
                {[PLATFORM_NAMES[d!.platform] ?? d!.platform, CLIENT_NAMES[d!.client] ?? d!.client, d!.clientVersion]
                  .filter(Boolean)
                  .join(" · ") || "Unknown device"}
              </p>
              <p className="pt-1 text-xs text-muted-foreground">
                The device describes itself; only you know whether you started this.
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm">Check that your device shows the same code:</p>
              <p className="font-mono text-2xl tracking-widest" data-testid="device-user-code">
                {d!.userCode}
              </p>
            </div>
            {!d!.sameNetwork && (
              <div role="note" className="nb-alert nb-alert--warning text-sm" data-testid="device-other-network">
                This request came from a different network
                {d!.requestIp ? ` (${d!.requestIp})` : ""} than this browser.
                That is expected for a remote server; otherwise, decline it.
              </div>
            )}
            <ErrorNote error={error} />
            <div className="flex gap-2">
              <Button className="flex-1" disabled={decide.isPending} onClick={() => void answer(true)}>
                Approve
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                disabled={decide.isPending}
                onClick={() => void answer(false)}
              >
                Decline
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </>
  );
}

// deviceReturnPath is where sign-in and registration return to: only the approval
// page itself, so a crafted ?next= cannot send a fresh session elsewhere.
export function deviceReturnPath(next: string | null | undefined): string {
  if (!next) return "";
  try {
    const u = new URL(next, "https://console.invalid");
    if (u.origin !== "https://console.invalid" || u.pathname !== "/device") return "";
    return u.pathname + u.search;
  } catch {
    return "";
  }
}
